import type { AestheticsRules } from "../domain/aestheticsRules";
import type { PogQuantityUnit } from "../adapters/pogToStudioItems";
import type { HierarchyMoveRules } from "../domain/hierarchyMoves/hierarchyMoveRules";

/** Profile contract version; readers must not guess at an unknown one. */
export const STUDIO_PROFILE_SCHEMA_VERSION = "1.0";
export const STUDIO_PRODUCT_CODE_MAPPING_COLUMN = "POG productCode mapping";

export interface StudioProfileColumns {
  sku: string;
  name: string;
}

export type StudioPogAttributeField =
  | "brand"
  | "variant"
  | "categoryCode"
  | "subCategory"
  | "size"
  | "color"
  | "cdt1"
  | "cdt2"
  | "cdt3";

export const STUDIO_POG_ATTRIBUTE_FIELDS: readonly StudioPogAttributeField[] = [
  "brand",
  "variant",
  "categoryCode",
  "subCategory",
  "size",
  "color",
  "cdt1",
  "cdt2",
  "cdt3",
];

export interface StudioProfileAttribute {
  /** Stable engine-facing key, deliberately independent of the source column. */
  key: string;
  label: string;
  column: string;
  /** PogNonCircular product field used to seed missing item-master rows. */
  pogField?: StudioPogAttributeField;
  /** The LIVE overlay attribute this level names; absent = the key already is it. */
  overlayAttr?: string;
}

/**
 * A CDT column does NOT carry one attribute category-wide, so each section
 * declares its own `live column → semantic attribute` mapping, resolved per
 * item. Everything downstream reads semantic names, never positional columns.
 */
export interface StudioSectionSemantics {
  /** Sections governed, case-insensitive. EMPTY = the fallback; at most one may be empty. */
  sections: readonly string[];
  /**
   * Extra conditions, as live column → the values it may hold. EVERY named
   * column must match, and the first matching mapping wins — order the
   * specific ones first. Sections are not always internally uniform.
   */
  when?: Readonly<Record<string, readonly string[]>>;
  /** Columns carrying NO level here; two columns may not map onto one attribute. */
  drop?: readonly string[];
  columns: Readonly<Record<string, string>>;
}

export interface StudioSemanticRoles {
  /** Attributes which together identify a merchandising block, major to minor. */
  blockIdentity: readonly string[];
  vertical: readonly string[];
  brand: string;
  tier: string;
  quantity: string;
  terminalVariant: string;
}

export interface StudioValueRule {
  attribute: string;
  /** Case-insensitive substring matches, in preferred order. */
  values: readonly string[];
}

export interface QuantityNormalizationSettings {
  enabled: boolean;
  inferFromName: boolean;
  zeroIsMissing: boolean;
  liquidOuncesAreFluid: boolean;
  /** Unit-family search order when more than one quantity occurs in a name. */
  unitPreference: readonly PogQuantityUnit[];
  /** Product-name terms which make an ambiguous ounce a fluid ounce. */
  liquidNameTerms: readonly string[];
  /** Normalized unit keyed by a case-insensitive source spelling. */
  unitAliases: Readonly<Record<string, PogQuantityUnit>>;
  /** Evidence required before an ambiguous value joins a unit family. */
  minimumFamilyEvidence: number;
  /** Inclusive ratio window. */
  reconciliationRange: { minimum: number; maximum: number };
  /** Attributes defining the evidence scope; false disables reconciliation. */
  reconciliationBlockAttributes: readonly string[] | false;
}

export interface VerticalBlockingOptions {
  /** Shelf-coverage share at which inference calls a value a vertical block. */
  blockShare: number;
  /** Maximum placement gap which inference still joins into one fragment. */
  fragmentJoinCm: number;
  minFillRatio: number;
  preserveRange: boolean;
  onlyIfBetter: boolean;
}

/** Merchant/category-specific config; source column names occur only here. */
export interface StudioProfile {
  schemaVersion: typeof STUDIO_PROFILE_SCHEMA_VERSION;
  profileVersion: string;
  merchantName: string;
  categoryName: string;
  /** POG product field matched against the item-master SKU column. */
  itemIdentifier: "productCode" | "upc" | "itemId";
  columns: StudioProfileColumns;
  attributes: readonly StudioProfileAttribute[];
  /** Per-section column semantics; absent means one attribute per column. */
  sectionSemantics?: readonly StudioSectionSemantics[];
  missingValues: readonly string[];
  semanticRoles: StudioSemanticRoles;
  firstValueRules: readonly StudioValueRule[];
  lastValueRules: readonly StudioValueRule[];
  /** Regex rules consumed by spine inference; false explicitly disables one. */
  preferredValueRules: { first: string | false; last: string | false };
  quantityNormalization: QuantityNormalizationSettings;
  aestheticsRules: AestheticsRules;
  hierarchyMoveRules: HierarchyMoveRules;
  verticalBlocking: VerticalBlockingOptions;
}

export class StudioProfileValidationError extends Error {
  readonly issues: readonly string[];

  constructor(issues: readonly string[]) {
    super(`Invalid Studio profile: ${issues.join("; ")}`);
    this.name = "StudioProfileValidationError";
    this.issues = issues;
  }
}

const isNonEmpty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const finiteInRange = (
  issues: string[],
  path: string,
  value: number,
  minimum: number,
  maximum = Number.POSITIVE_INFINITY,
): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    issues.push(`${path} must be a finite number from ${minimum} to ${maximum}`);
  }
};

const sumIs = (values: readonly number[], expected: number): boolean =>
  Math.abs(values.reduce((sum, value) => sum + value, 0) - expected) <= 1e-9;

export const terminalLastValuesOf = (profile: StudioProfile): readonly string[] =>
  profile.lastValueRules
    .filter((rule) => rule.attribute === profile.semanticRoles.terminalVariant)
    .flatMap((rule) => rule.values);

/** Throws one error listing every profile defect found. */
export const validateStudioProfile = (profile: StudioProfile): void => {
  const issues: string[] = [];
  if (profile.schemaVersion !== STUDIO_PROFILE_SCHEMA_VERSION) {
    issues.push(
      `schemaVersion must be ${STUDIO_PROFILE_SCHEMA_VERSION}, received ${String(profile.schemaVersion)}`,
    );
  }
  const requiredTextFields: readonly (readonly [string, string])[] = [
    ["profileVersion", profile.profileVersion],
    ["merchantName", profile.merchantName],
    ["categoryName", profile.categoryName],
    ["columns.sku", profile.columns.sku],
    ["columns.name", profile.columns.name],
  ];
  for (const [path, value] of requiredTextFields) {
    if (!isNonEmpty(value)) issues.push(`${path} must be non-empty`);
  }
  if (profile.columns.sku.trim() === profile.columns.name.trim()) {
    issues.push("columns.sku and columns.name must be different");
  }
  if (
    [
      profile.columns.sku,
      profile.columns.name,
      ...profile.attributes.map((attribute) => attribute.column),
    ].some((column) => column.trim() === STUDIO_PRODUCT_CODE_MAPPING_COLUMN)
  ) {
    issues.push(
      `source columns must not use reserved column ${JSON.stringify(STUDIO_PRODUCT_CODE_MAPPING_COLUMN)}`,
    );
  }
  if (!["productCode", "upc", "itemId"].includes(profile.itemIdentifier)) {
    issues.push("itemIdentifier must be productCode, upc, or itemId");
  }

  const keys = new Set<string>();
  const columns = new Set<string>([profile.columns.sku.trim(), profile.columns.name.trim()]);
  // Two profile levels naming ONE overlay attribute: the last would win silently.
  const overlayAttrs = new Set<string>();
  profile.attributes.forEach((attribute, index) => {
    const path = `attributes[${index}]`;
    const key = attribute.key.trim();
    const column = attribute.column.trim();
    if (attribute.overlayAttr !== undefined) {
      const overlayAttr = attribute.overlayAttr.trim();
      if (!overlayAttr) issues.push(`${path}.overlayAttr must be non-empty when present`);
      else if (overlayAttrs.has(overlayAttr))
        issues.push(`${path}.overlayAttr duplicates ${JSON.stringify(overlayAttr)}`);
      else overlayAttrs.add(overlayAttr);
    }
    if (!key) issues.push(`${path}.key must be non-empty`);
    else if (keys.has(key)) issues.push(`${path}.key duplicates ${JSON.stringify(key)}`);
    else keys.add(key);
    if (!isNonEmpty(attribute.label)) issues.push(`${path}.label must be non-empty`);
    if (!column) issues.push(`${path}.column must be non-empty`);
    else if (columns.has(column))
      issues.push(`${path}.column duplicates ${JSON.stringify(column)}`);
    else columns.add(column);
    if (
      attribute.pogField !== undefined &&
      !STUDIO_POG_ATTRIBUTE_FIELDS.includes(attribute.pogField)
    ) {
      issues.push(`${path}.pogField is unsupported`);
    }
  });
  if (profile.attributes.length === 0) issues.push("attributes must not be empty");

  /* Section semantics: a section claimed twice, and two columns of one section
     naming the same semantic attribute, are both errors — either resolves by
     key order. A mapping naming a column the live data lacks is NOT an error;
     the adapter drops the pair rather than manufacture an empty level. */
  const mappedSections = new Set<string>();
  let fallbackMappings = 0;
  (profile.sectionSemantics ?? []).forEach((mapping, index) => {
    const path = `sectionSemantics[${index}]`;
    if (mapping.sections.length === 0) {
      fallbackMappings += 1;
      if (fallbackMappings > 1) {
        issues.push(`${path}.sections is empty, but a fallback mapping already exists`);
      }
    }
    mapping.sections.forEach((section, sectionIndex) => {
      if (!isNonEmpty(section)) {
        issues.push(`${path}.sections[${sectionIndex}] must be non-empty`);
        return;
      }
      /* A section may repeat only while `when` tells the repeats apart; two
         UNCONDITIONAL entries and the first-match rule silently picks one. */
      const normalized = section.trim().toLowerCase();
      if (Object.keys(mapping.when ?? {}).length === 0) {
        if (mappedSections.has(normalized)) {
          issues.push(
            `${path}.sections duplicates ${JSON.stringify(section)} with no "when" to tell them apart`,
          );
        }
        mappedSections.add(normalized);
      }
    });
    (mapping.drop ?? []).forEach((column, dropIndex) => {
      if (!isNonEmpty(column)) issues.push(`${path}.drop[${dropIndex}] must be non-empty`);
      if (column in mapping.columns) {
        issues.push(`${path}.drop[${dropIndex}] is also mapped in columns`);
      }
    });
    for (const [column, values] of Object.entries(mapping.when ?? {})) {
      if (!isNonEmpty(column)) issues.push(`${path}.when contains an empty column`);
      if (values.length === 0) {
        issues.push(`${path}.when.${JSON.stringify(column)} must list at least one value`);
      }
    }
    const entries = Object.entries(mapping.columns);
    if (entries.length === 0) issues.push(`${path}.columns must not be empty`);
    const semanticAttrs = new Set<string>();
    for (const [column, semantic] of entries) {
      if (!isNonEmpty(column)) issues.push(`${path}.columns contains an empty column`);
      if (!isNonEmpty(semantic)) {
        issues.push(`${path}.columns.${JSON.stringify(column)} must name a semantic attribute`);
        continue;
      }
      if (semanticAttrs.has(semantic)) {
        issues.push(`${path}.columns maps two columns onto ${JSON.stringify(semantic)}`);
      }
      semanticAttrs.add(semantic);
    }
  });

  const requireRole = (path: string, key: string): void => {
    if (!isNonEmpty(key)) issues.push(`${path} must be non-empty`);
    else if (!keys.has(key))
      issues.push(`${path} references unknown attribute ${JSON.stringify(key)}`);
  };
  requireRole("semanticRoles.brand", profile.semanticRoles.brand);
  requireRole("semanticRoles.tier", profile.semanticRoles.tier);
  requireRole("semanticRoles.quantity", profile.semanticRoles.quantity);
  requireRole("semanticRoles.terminalVariant", profile.semanticRoles.terminalVariant);
  const collectionRoles: readonly ("blockIdentity" | "vertical")[] = ["blockIdentity", "vertical"];
  for (const role of collectionRoles) {
    if (profile.semanticRoles[role].length === 0) {
      issues.push(`semanticRoles.${role} must not be empty`);
    }
    const seen = new Set<string>();
    profile.semanticRoles[role].forEach((key, index) => {
      requireRole(`semanticRoles.${role}[${index}]`, key);
      if (seen.has(key)) issues.push(`semanticRoles.${role} duplicates ${JSON.stringify(key)}`);
      seen.add(key);
    });
  }

  const valueRuleCollections: readonly (readonly [string, readonly StudioValueRule[]])[] = [
    ["firstValueRules", profile.firstValueRules],
    ["lastValueRules", profile.lastValueRules],
  ];
  for (const [ruleName, rules] of valueRuleCollections) {
    rules.forEach((rule, index) => {
      requireRole(`${ruleName}[${index}].attribute`, rule.attribute);
      if (rule.values.length === 0) issues.push(`${ruleName}[${index}].values must not be empty`);
      rule.values.forEach((value, valueIndex) => {
        if (!isNonEmpty(value)) issues.push(`${ruleName}[${index}].values[${valueIndex}] is empty`);
      });
    });
  }

  const quantity = profile.quantityNormalization;
  finiteInRange(
    issues,
    "quantityNormalization.minimumFamilyEvidence",
    quantity.minimumFamilyEvidence,
    1,
  );
  if (!Number.isInteger(quantity.minimumFamilyEvidence)) {
    issues.push("quantityNormalization.minimumFamilyEvidence must be an integer");
  }
  finiteInRange(
    issues,
    "quantityNormalization.reconciliationRange.minimum",
    quantity.reconciliationRange.minimum,
    0,
  );
  finiteInRange(
    issues,
    "quantityNormalization.reconciliationRange.maximum",
    quantity.reconciliationRange.maximum,
    quantity.reconciliationRange.minimum,
  );
  quantity.unitPreference.forEach((value, index) => {
    if (!isNonEmpty(value)) issues.push(`quantityNormalization.unitPreference[${index}] is empty`);
  });
  Object.entries(quantity.unitAliases).forEach(([source, target]) => {
    if (!isNonEmpty(source)) issues.push("quantityNormalization.unitAliases contains an empty key");
    if (!["fl oz", "l", "ml", "oz", "lb", "loads", "ct"].includes(target)) {
      issues.push(
        `quantityNormalization.unitAliases.${JSON.stringify(source)} has unsupported unit ${JSON.stringify(target)}`,
      );
    }
  });
  quantity.liquidNameTerms.forEach((value, index) => {
    if (!isNonEmpty(value)) issues.push(`quantityNormalization.liquidNameTerms[${index}] is empty`);
  });
  if (quantity.reconciliationBlockAttributes !== false) {
    quantity.reconciliationBlockAttributes.forEach((value, index) => {
      if (!isNonEmpty(value)) {
        issues.push(`quantityNormalization.reconciliationBlockAttributes[${index}] is empty`);
      }
    });
  }
  const preferredValueSources: readonly (readonly [string, string | false])[] = [
    ["preferredValueRules.first", profile.preferredValueRules.first],
    ["preferredValueRules.last", profile.preferredValueRules.last],
  ];
  for (const [path, source] of preferredValueSources) {
    if (source === false) continue;
    if (!isNonEmpty(source)) {
      issues.push(`${path} must be a non-empty regex or false`);
      continue;
    }
    try {
      new RegExp(source, "i");
    } catch {
      issues.push(`${path} must be a valid regex`);
    }
  }

  const aesthetics = profile.aestheticsRules;
  finiteInRange(issues, "aestheticsRules.bulkPounds", aesthetics.bulkPounds, 0);
  finiteInRange(issues, "aestheticsRules.lowerShelfShare", aesthetics.lowerShelfShare, 0, 1);
  finiteInRange(issues, "aestheticsRules.jitterPacks", aesthetics.jitterPacks, 0);
  finiteInRange(issues, "aestheticsRules.raggedZeroPacks", aesthetics.raggedZeroPacks, 0);
  finiteInRange(issues, "aestheticsRules.flagsPerHundredZero", aesthetics.flagsPerHundredZero, 0);
  Object.entries(aesthetics.severityPenalty).forEach(([key, value]) =>
    finiteInRange(issues, `aestheticsRules.severityPenalty.${key}`, value, 0),
  );
  Object.entries(aesthetics.weights).forEach(([key, value]) =>
    finiteInRange(issues, `aestheticsRules.weights.${key}`, value, 0, 100),
  );
  if (!sumIs(Object.values(aesthetics.weights), 100)) {
    issues.push("aestheticsRules.weights must sum to 100");
  }
  const aestheticSplits: readonly (readonly [string, Readonly<Record<string, number>>])[] = [
    ["aestheticsRules.blockingSplit", aesthetics.blockingSplit],
    ["aestheticsRules.cdtSplit", aesthetics.cdtSplit],
  ];
  for (const [path, split] of aestheticSplits) {
    Object.entries(split).forEach(([key, value]) =>
      finiteInRange(issues, `${path}.${key}`, value, 0, 1),
    );
    if (!sumIs(Object.values(split), 1)) issues.push(`${path} must sum to 1`);
  }
  requireRole("aestheticsRules.brandAttr", aesthetics.brandAttr);
  requireRole("aestheticsRules.tierAttr", aesthetics.tierAttr);
  requireRole("aestheticsRules.sizeAttr", aesthetics.sizeAttr);
  requireRole("aestheticsRules.flavourAttr", aesthetics.flavourAttr);
  aesthetics.verticalAttrs.forEach((key, index) =>
    requireRole(`aestheticsRules.verticalAttrs[${index}]`, key),
  );
  const semanticAestheticPairs: readonly (readonly [
    "brandAttr" | "tierAttr" | "sizeAttr" | "flavourAttr",
    string,
  ])[] = [
    ["brandAttr", profile.semanticRoles.brand],
    ["tierAttr", profile.semanticRoles.tier],
    ["sizeAttr", profile.semanticRoles.quantity],
    ["flavourAttr", profile.semanticRoles.terminalVariant],
  ];
  semanticAestheticPairs.forEach(([key, expected]) => {
    if (aesthetics[key] !== expected) {
      issues.push(
        `aestheticsRules.${key} must match its semantic role ${JSON.stringify(expected)}`,
      );
    }
  });
  if (JSON.stringify(aesthetics.verticalAttrs) !== JSON.stringify(profile.semanticRoles.vertical)) {
    issues.push("aestheticsRules.verticalAttrs must match semanticRoles.vertical");
  }
  if (JSON.stringify(aesthetics.lastInBlock) !== JSON.stringify(terminalLastValuesOf(profile))) {
    issues.push(
      "aestheticsRules.lastInBlock must match lastValueRules for semanticRoles.terminalVariant",
    );
  }

  const hierarchy = profile.hierarchyMoveRules;
  finiteInRange(issues, "hierarchyMoveRules.minFacingsPerShelf", hierarchy.minFacingsPerShelf, 0);
  finiteInRange(
    issues,
    "hierarchyMoveRules.roundTripFloorShare",
    hierarchy.roundTripFloorShare,
    0,
    1,
  );
  finiteInRange(issues, "hierarchyMoveRules.fillFloorShare", hierarchy.fillFloorShare, 0, 1);
  finiteInRange(
    issues,
    "hierarchyMoveRules.fillDropTolerancePts",
    hierarchy.fillDropTolerancePts,
    0,
    100,
  );

  finiteInRange(issues, "verticalBlocking.blockShare", profile.verticalBlocking.blockShare, 0, 1);
  finiteInRange(
    issues,
    "verticalBlocking.fragmentJoinCm",
    profile.verticalBlocking.fragmentJoinCm,
    0,
  );
  finiteInRange(
    issues,
    "verticalBlocking.minFillRatio",
    profile.verticalBlocking.minFillRatio,
    0,
    1,
  );

  if (issues.length > 0) throw new StudioProfileValidationError(issues);
};
